import { motion } from "framer-motion";
import { Zap, Wind, Volume2, Snowflake, ShieldCheck, BatteryCharging, Sparkles } from "lucide-react";
import { Eyebrow, MaskLine, ease } from "@/components/site/Reveal";
import { CountUp } from "@/components/site/CountUp";

function Tile({ className = "", children, delay = 0, testid }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, ease, delay }}
      whileHover={{ y: -4 }}
      className={`group relative overflow-hidden border border-white/10 bg-surface p-6 md:p-8 transition-colors duration-300 hover:border-[rgba(212,230,10,0.45)] ${className}`}
    >
      {children}
    </motion.div>
  );
}

function SmallTile({ icon: Icon, title, text, delay, testid }) {
  return (
    <Tile delay={delay} testid={testid} className="flex flex-col justify-between min-h-[220px]">
      <Icon className="h-8 w-8 text-volt transition-transform duration-500 group-hover:rotate-12" />
      <div>
        <h3 className="font-display text-3xl font-extrabold uppercase leading-none">{title}</h3>
        <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{text}</p>
      </div>
    </Tile>
  );
}

export function Bento() {
  return (
    <section id="why-battery" data-testid="why-battery-section" className="relative bg-gunmetal py-24 md:py-36">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Eyebrow>Why Battery</Eyebrow>
        <h2 className="mt-5 mb-12 md:mb-16 font-display text-[12.5vw] sm:text-6xl md:text-8xl font-black uppercase leading-[0.85]" style={{ fontWeight: 900 }}>
          <MaskLine inView>Outperforming</MaskLine>
          <MaskLine inView delay={0.1}>the <span className="text-rescue">gas engine.</span></MaskLine>
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[260px]">
          <Tile testid="bento-torque" className="sm:col-span-2 lg:row-span-2 min-h-[420px] p-0 md:p-0">
            <img src="/img/cutter-wet.webp" alt="BAMF Battery Cutter wet cutting concrete" className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-1000 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="relative flex h-full flex-col justify-end p-6 md:p-10">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-volt">Unrivaled torque density</p>
              <p className="mt-4 font-display text-[7rem] md:text-[10rem] font-black leading-[0.8]" style={{ fontWeight: 900 }} data-testid="bento-torque-number"><CountUp value={10.7} decimals={1} /><span className="text-volt text-5xl md:text-7xl">HP</span></p>
              <p className="mt-3 max-w-md text-zinc-200">Instant torque the moment you squeeze. No warm-up, no stall, no choke.</p>
            </div>
          </Tile>
          <Tile testid="bento-emissions" delay={0.05} className="sm:col-span-2 flex flex-col justify-between min-h-[220px]">
            <div className="flex items-start justify-between">
              <Wind className="h-8 w-8 text-volt" />
              <span className="font-display text-8xl font-black leading-none text-white/10 group-hover:text-volt/20 transition-colors">0</span>
            </div>
            <div>
              <h3 className="font-display text-4xl font-extrabold uppercase leading-none">Zero Emissions</h3>
              <p className="mt-3 max-w-md text-sm text-zinc-300">No exhaust in confined spaces, collapsed structures or vehicle interiors.</p>
            </div>
          </Tile>
          <SmallTile testid="bento-noise" icon={Volume2} title="Low-Noise" text="Enhanced scene communication. Your crew can actually hear each other." delay={0.1} />
          <SmallTile testid="bento-spark" icon={Sparkles} title="Spark-Free" text={'"Cold" cutting with spark-free operation.'} delay={0.15} />
          <Tile testid="bento-ecosystem" delay={0.1} className="sm:col-span-2 flex flex-col justify-between min-h-[220px] border-[rgba(212,230,10,0.45)]">
            <BatteryCharging className="h-8 w-8 text-volt" />
            <div className="text-white">
              <h3 className="font-display text-4xl font-black uppercase leading-none">One Battery. <span className="text-volt">Whole Ecosystem.</span></h3>
              <p className="mt-3 max-w-md text-sm text-zinc-300">Universal 8 Ah power across saws, chargers and the LPS-2400 power station.</p>
            </div>
          </Tile>
          <SmallTile testid="bento-climate" icon={Snowflake} title="Arctic-Ready" text="All-climate, all-weather readiness for every call." delay={0.15} />
          <SmallTile testid="bento-kickback" icon={ShieldCheck} title="Zero-Kickback" text="Stable control on the Twin Saw for safer cuts." delay={0.2} />
        </div>
        <div className="mt-3 flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest text-zinc-400">
          <Zap className="h-3 w-3 text-volt" /> Figures from BAMF product brochure.
        </div>
      </div>
    </section>
  );
}
